/**
 * ProductPickerModal.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import { queryMatchesSearchText, productRowSearchHaystack } from '../utils/siteSearch';
import './ProductPickerModal.css';

const ProductPickerModal = ({
  isOpen,
  onClose,
  onSelect,
  multiSelect = false,
  selectedProductIds = [],
  onSelectionChange,
  title = '🛍️ Select Product',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tempSelection, setTempSelection] = useState([]);
  const wasOpenRef = useRef(false);

  const fetchProducts = async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) setLoading(true);
      const response = await fetch('/api/products');
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      setProducts(Array.isArray(data) ? data : (data.products || []));
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Only initialize when the modal opens — not when parent selection changes (avoids flicker)
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (!justOpened) return;

    setSearchTerm('');
    if (multiSelect) {
      setTempSelection([...selectedProductIds]);
    }
    fetchProducts({ showLoading: products.length === 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedProductIds read at open only
  }, [isOpen]);

  const handleSelect = (product) => {
    if (multiSelect) {
      setTempSelection((prev) =>
        prev.includes(product.id)
          ? prev.filter((id) => id !== product.id)
          : [...prev, product.id]
      );
      return;
    }
    onSelect(product);
    onClose();
  };

  const handleApplyMulti = () => {
    if (typeof onSelectionChange === 'function') {
      onSelectionChange(tempSelection);
    }
    onClose();
  };

  const handleCancelMulti = () => {
    setTempSelection([...selectedProductIds]);
    onClose();
  };

  const filteredProducts = products.filter(product =>
    queryMatchesSearchText(productRowSearchHaystack(product), searchTerm, { title: product.name }),
  );

  if (!isOpen) return null;

  return (
    <div className="product-picker-overlay" onClick={multiSelect ? handleCancelMulti : onClose}>
      <div className="product-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="product-picker-header">
          <h3>{title}</h3>
          <button className="close-btn" onClick={multiSelect ? handleCancelMulti : onClose}>×</button>
        </div>

        <div className="product-picker-search">
          <input
            type="text"
            placeholder="Search by name, SKU, author, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            autoFocus
          />
        </div>

        <div className="product-picker-content">
          {loading ? (
            <div className="loading-state">Loading products...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="empty-state">
              <p>No products found</p>
              {searchTerm && <p className="hint">Try a different search term</p>}
            </div>
          ) : (
            <div className="products-list">
              {filteredProducts.map(product => {
                const isSelected = multiSelect && tempSelection.includes(product.id);
                return (
                <div
                  key={product.id}
                  className={`product-item${isSelected ? ' product-item-selected' : ''}`}
                  onClick={() => handleSelect(product)}
                >
                  {multiSelect && (
                    <div className="product-picker-check" aria-hidden="true">
                      {isSelected ? '✓' : ''}
                    </div>
                  )}
                  {product.image && (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="product-image"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="product-info">
                    <div className="product-header">
                      <span className="product-name">{product.name}</span>
                      <span className="product-id">ID: {product.id}</span>
                    </div>
                    <div className="product-meta">
                      {product.author && (
                        <span className="product-author">👤 {product.author}</span>
                      )}
                      {product.category && (
                        <span className="product-category">📂 {product.category}</span>
                      )}
                      {product.sku && (
                        <span className="product-sku">SKU: {product.sku}</span>
                      )}
                    </div>
                    <div className="product-footer">
                      <span className="product-type">{product.type || 'Product'}</span>
                      <span className="product-price">${parseFloat(product.price).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>

        <div className="product-picker-footer">
          {multiSelect ? (
            <div className="product-picker-footer-actions">
              <span className="footer-hint">
                {tempSelection.length} selected — click products to toggle
              </span>
              <div className="product-picker-footer-buttons">
                <button type="button" className="btn btn-secondary" onClick={handleCancelMulti}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleApplyMulti}>
                  Apply ({tempSelection.length})
                </button>
              </div>
            </div>
          ) : (
            <p className="footer-hint">
              💡 Click on a product to select it
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductPickerModal;

